/**
 * useTransactionState — generic transaction state machine hook
 *
 * Manages TransactionStatus lifecycle: idle → preparing → awaiting_signature → pending → confirmed | failed | rejected
 * Shared across deploy (Epic 2), swap (Epic 5), and fee claims (Epic 6).
 *
 * ARCH-25: Transaction lifecycle state machine
 */

import type { TransactionStatus } from "@repo/shared-types";
import { useCallback, useMemo, useState } from "react";

export type TransactionState = {
	status: TransactionStatus;
	txHash: `0x${string}` | undefined;
	error: string | undefined;
};

export type TransactionStateActions = {
	/** Current transaction state */
	state: TransactionState;
	/** Transition to preparing */
	setPreparing: () => void;
	/** Transition to awaiting_signature */
	setAwaitingSignature: () => void;
	/** Transition to pending with tx hash */
	setPending: (txHash: `0x${string}`) => void;
	/** Transition to confirmed */
	setConfirmed: () => void;
	/** Transition to failed with error message */
	setFailed: (error: string) => void;
	/** Transition to rejected (user cancelled) */
	setRejected: () => void;
	/** Reset to idle */
	reset: () => void;
};

const INITIAL_STATE: TransactionState = {
	status: "idle",
	txHash: undefined,
	error: undefined,
};

export function useTransactionState(): TransactionStateActions {
	const [state, setState] = useState<TransactionState>(INITIAL_STATE);

	const setPreparing = useCallback(() => {
		setState({ status: "preparing", txHash: undefined, error: undefined });
	}, []);

	const setAwaitingSignature = useCallback(() => {
		setState((prev) => ({ ...prev, status: "awaiting_signature", error: undefined }));
	}, []);

	const setPending = useCallback((txHash: `0x${string}`) => {
		setState({ status: "pending", txHash, error: undefined });
	}, []);

	const setConfirmed = useCallback(() => {
		setState((prev) => ({ ...prev, status: "confirmed", error: undefined }));
	}, []);

	const setFailed = useCallback((error: string) => {
		setState((prev) => ({ ...prev, status: "failed", error }));
	}, []);

	const setRejected = useCallback(() => {
		setState((prev) => ({ ...prev, status: "rejected", error: undefined }));
	}, []);

	const reset = useCallback(() => {
		setState(INITIAL_STATE);
	}, []);

	return useMemo(
		() => ({
			state,
			setPreparing,
			setAwaitingSignature,
			setPending,
			setConfirmed,
			setFailed,
			setRejected,
			reset,
		}),
		[
			state,
			setPreparing,
			setAwaitingSignature,
			setPending,
			setConfirmed,
			setFailed,
			setRejected,
			reset,
		]
	);
}
